import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Mic, MicOff, Loader2, Play, Pause, Save, 
  X, ChevronLeft, Sparkles, RefreshCw 
} from 'lucide-react';

interface Recording {
  id: string;
  prompt: string;
  response: string;
  audioUrl?: string;
  createdAt: Date;
  rating?: number;
  category?: string;
  duration?: number;
}

export const MobileRecorder: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const recordingTimeRef = useRef<number>(0);

  useEffect(() => {
    // Check if mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile && window.innerWidth > 768) {
      // Optionally redirect to desktop view
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startRecording = async () => {
    try {
      console.log('Starting recording...');
      
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia is not supported in this browser');
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      console.log('Got media stream:', stream);
      
      // Check for MediaRecorder support
      if (!window.MediaRecorder) {
        throw new Error('MediaRecorder is not supported in this browser');
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      setRecordingTime(0);
      recordingTimeRef.current = 0;

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          recordingTimeRef.current = newTime;
          return newTime;
        });
      }, 1000);

      mediaRecorderRef.current.ondataavailable = (event) => {
        console.log('Data available:', event.data.size);
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        console.log('Recording stopped, processing...');
        const finalRecordingTime = recordingTimeRef.current;
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        
        setIsProcessing(true);
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        console.log('Audio blob size:', audioBlob.size);
        console.log('Recording duration:', finalRecordingTime, 'seconds');
        
        // Process the recording with duration info
        await processRecording(audioBlob, finalRecordingTime);
        
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.onerror = (event) => {
        console.error('MediaRecorder error:', event);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      console.log('Recording started');
    } catch (error: any) {
      console.error('Error accessing microphone:', error);
      
      // More specific error messages
      let errorMessage = 'Unable to access microphone. ';
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage += 'Please allow microphone access in your browser settings.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage += 'No microphone found. Please connect a microphone.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorMessage += 'Microphone is already in use by another application.';
      } else if (error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError') {
        errorMessage += 'Microphone does not meet the requirements.';
      } else {
        errorMessage += error.message || 'Please check your browser settings.';
      }
      
      alert(errorMessage);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processRecording = async (audioBlob: Blob, duration: number = 0) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      console.log('📊 Processing recording:', {
        blobSize: audioBlob.size,
        blobType: audioBlob.type,
        duration: duration,
        apiUrl: apiUrl
      });
      
      // Convert blob to base64 for API transmission
      const arrayBuffer = await audioBlob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      const chunkSize = 0x8000; // Process in chunks to avoid call stack issues
      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
        binary += String.fromCharCode.apply(null, Array.from(chunk));
      }
      const base64Audio = btoa(binary);
      
      let transcriptText = '';
      
      try {
        // Try to get real transcription from API
        const transcribeResponse = await fetch(`${apiUrl}/api/audio-input/transcribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            audio: base64Audio,
            format: 'webm' // WebM is the default format from MediaRecorder
          })
        });
        
        if (transcribeResponse.ok) {
          const transcribeData = await transcribeResponse.json();
          transcriptText = transcribeData.text;
          console.log('✅ Got real transcription:', transcriptText);
        } else {
          const errorText = await transcribeResponse.text();
          console.error('❌ Transcription API error:', transcribeResponse.status, errorText);
          throw new Error(`Transcription API error: ${transcribeResponse.status}`);
        }
      } catch (transcribeError) {
        console.log('Transcription failed, using fallback:', transcribeError);
        
        // Fallback to demo transcripts if transcription fails
        const demoTranscripts = duration < 3 ? [
          "I need a quick boost.",
          "Help me get started.",
          "I'm feeling stuck.",
          "Give me some energy.",
          "I need a push."
        ] : duration < 10 ? [
          "I'm struggling with self-doubt and need encouragement.",
          "I'm feeling overwhelmed with everything on my plate.",
          "I keep procrastinating and can't seem to start.",
          "I'm worried I'm not good enough for this.",
          "I need help believing in myself again."
        ] : [
          "I've been working really hard but I'm not seeing the results I want, and it's making me question if I'm on the right path.",
          "I'm facing a big challenge and I'm scared of failing. I need courage to take the next step.",
          "Everyone around me seems to be doing better and I can't help but compare myself. I need to find my own path.",
          "I started with so much enthusiasm but now I'm losing motivation. Help me reconnect with my purpose.",
          "I'm dealing with setbacks and it feels like nothing is going right. I need perspective and hope."
        ];
        
        transcriptText = demoTranscripts[Math.floor(Math.random() * demoTranscripts.length)];
      }
      
      setTranscript(transcriptText);
      
      // Get AI response based on the transcript
      try {
        // Add a timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
        
        const response = await fetch(`${apiUrl}/api/selftalk/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            prompt: transcriptText,
            category: 'motivation'
          }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          const aiResponseText = data.message;
          setAiResponse(aiResponseText);
          await generateAudio(aiResponseText);
        } else {
          console.log('API returned error status:', response.status);
          throw new Error(`API response not ok: ${response.status}`);
        }
      } catch (apiError: any) {
        console.log('API error:', apiError.message);
        // Fallback responses if API fails
        const fallbackResponses = [
          "You have incredible strength within you. Every challenge you're facing is shaping you into a stronger, wiser person. Remember, the most successful people aren't those who never fail, but those who never quit. Take a deep breath, trust yourself, and take one small step forward. You've got this!",
          "Listen to me: you are exactly where you need to be right now. Every step you've taken has led you here, and every step forward is progress. Your journey is unique, your pace is perfect, and your potential is unlimited. Keep moving forward, one moment at a time.",
          "I believe in you, and here's why: you're here, seeking growth, seeking support. That takes courage. The fact that you're reaching out shows your strength, not weakness. You have everything within you to overcome this moment. Trust the process, trust yourself.",
          "This feeling won't last forever. You've overcome challenges before, and you'll overcome this one too. Your resilience is stronger than any obstacle. Take it one breath, one moment, one step at a time. You're doing better than you think.",
          "Remember why you started. That fire inside you hasn't gone out - it's just waiting for you to fan the flames again. You have a purpose, a mission, a reason to keep going. The world needs what you have to offer. Don't give up now."
        ];
        
        const aiResponseText = fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)];
        setAiResponse(aiResponseText);
        await generateAudio(aiResponseText);
      }
      
    } catch (error) {
      console.error('Error processing recording:', error);
      
      // Final fallback
      const demoTranscript = "I need motivation to keep going.";
      const demoResponse = "You have incredible strength within you. Every challenge you're facing is shaping you into a stronger, wiser person. Remember, the most successful people aren't those who never fail, but those who never quit. Take a deep breath, trust yourself, and take one small step forward. You've got this!";
      
      setTranscript(demoTranscript);
      setAiResponse(demoResponse);
      await generateAudio(demoResponse);
    } finally {
      setIsProcessing(false);
    }
  };

  const generateAudio = async (text: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      
      // Get voice from settings
      const userSettings = localStorage.getItem('userSettings');
      const settings = userSettings ? JSON.parse(userSettings) : {};
      const voice = settings.voice || 'rachel';
      
      const response = await fetch(`${apiUrl}/api/audio/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.audio_url) {
          setAudioUrl(data.audio_url);
          return;
        }
      }
    } catch (error) {
      console.log('ElevenLabs not available, using browser TTS');
    }
    
    // Fallback to browser TTS
    if ('speechSynthesis' in window) {
      // Get speech rate from settings
      const userSettings = localStorage.getItem('userSettings');
      const settings = userSettings ? JSON.parse(userSettings) : {};
      const speechRate = settings.speechRate || 0.9;
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = speechRate;
      speechSynthesis.speak(utterance);
    }
  };

  const togglePlayback = () => {
    if (!audioUrl && !aiResponse) return;

    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
      } else {
        speechSynthesis.cancel();
      }
      setIsPlaying(false);
    } else {
      if (audioUrl && audioRef.current) {
        audioRef.current.play();
      } else if (aiResponse) {
        // Get speech rate from settings
        const userSettings = localStorage.getItem('userSettings');
        const settings = userSettings ? JSON.parse(userSettings) : {};
        const speechRate = settings.speechRate || 0.9;
        
        const utterance = new SpeechSynthesisUtterance(aiResponse);
        utterance.rate = speechRate;
        utterance.onend = () => setIsPlaying(false);
        speechSynthesis.speak(utterance);
      }
      setIsPlaying(true);
    }
  };

  const saveRecording = async () => {
    const recording: Recording = {
      id: Date.now().toString(),
      prompt: transcript,
      response: aiResponse,
      audioUrl: audioUrl,
      createdAt: new Date(),
      duration: recordingTime
    };

    // Save to localStorage for now
    const existingRecordings = JSON.parse(localStorage.getItem('recordings') || '[]');
    existingRecordings.unshift(recording);
    localStorage.setItem('recordings', JSON.stringify(existingRecordings.slice(0, 100)));

    // Navigate to recordings library
    navigate('/recordings');
  };

  const regenerateResponse = async () => {
    if (!transcript) return;
    setIsProcessing(true);
    
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/selftalk/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          prompt: transcript,
          category: 'motivation'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResponse(data.message);
        await generateAudio(data.message);
      }
    } catch (error) {
      console.error('Error regenerating:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/20 backdrop-blur-lg border-b border-white/10">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold">AI Coach Recorder</h1>
          <button
            onClick={() => navigate('/library')}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <Save className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 pb-4">
        {!aiResponse ? (
          // Recording Interface
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="text-center mb-8">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-purple-400" />
              <h2 className="text-2xl font-bold mb-2">
                {isRecording ? 'Recording...' : 'Share Your Thoughts'}
              </h2>
              <p className="text-gray-400">
                {isRecording 
                  ? 'Speak your mind. Share what you need to hear.'
                  : 'Tap the microphone to start recording'}
              </p>
            </div>

            {/* Recording Button */}
            <div className="relative">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing}
                className={`w-32 h-32 rounded-full flex items-center justify-center transition-all transform ${
                  isRecording 
                    ? 'bg-red-500 hover:bg-red-600 scale-110 animate-pulse' 
                    : isProcessing
                    ? 'bg-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 hover:scale-105'
                }`}
              >
                {isProcessing ? (
                  <Loader2 className="w-12 h-12 animate-spin" />
                ) : isRecording ? (
                  <MicOff className="w-12 h-12" />
                ) : (
                  <Mic className="w-12 h-12" />
                )}
              </button>
              
              {isRecording && (
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
                  <span className="text-xl font-mono">{formatTime(recordingTime)}</span>
                </div>
              )}
            </div>

            {isProcessing && (
              <div className="mt-8 text-center">
                <p className="text-gray-400 animate-pulse">Processing your message...</p>
              </div>
            )}
          </div>
        ) : (
          // Response Interface
          <div className="space-y-6">
            {/* Transcript */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
              <h3 className="text-sm font-medium text-gray-400 mb-2">Your Message:</h3>
              <p className="text-gray-200">{transcript}</p>
            </div>

            {/* AI Response */}
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-sm font-medium text-purple-300">AI Coach Response:</h3>
                <button
                  onClick={regenerateResponse}
                  disabled={isProcessing}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                  title="Regenerate response"
                >
                  <RefreshCw className={`w-4 h-4 ${isProcessing ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <p className="text-lg leading-relaxed">{aiResponse}</p>
            </div>

            {/* Audio Player */}
            {audioUrl && (
              <audio
                ref={audioRef}
                src={audioUrl}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                className="hidden"
              />
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={togglePlayback}
                className="flex-1 flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all"
              >
                {isPlaying ? (
                  <>
                    <Pause className="w-5 h-5" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Play Audio
                  </>
                )}
              </button>

              <button
                onClick={saveRecording}
                className="flex-1 flex items-center justify-center gap-2 py-4 bg-green-600 rounded-xl font-semibold hover:bg-green-700 transition-colors"
              >
                <Save className="w-5 h-5" />
                Save
              </button>
            </div>

            {/* Record Another */}
            <button
              onClick={() => {
                setAiResponse('');
                setTranscript('');
                setAudioUrl('');
                setRecordingTime(0);
                recordingTimeRef.current = 0;
                setIsProcessing(false);
                setIsPlaying(false);
                // Stop any playing audio
                if (audioRef.current) {
                  audioRef.current.pause();
                }
                speechSynthesis.cancel();
              }}
              className="w-full py-4 bg-white/10 backdrop-blur-sm rounded-xl font-semibold hover:bg-white/20 transition-colors border border-white/20"
            >
              Record Another
            </button>
          </div>
        )}
      </div>
    </div>
  );
};